import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { omit } from 'lodash';

import { useSearchContext } from '@app/contexts';
import { useBranding, useQueryParams } from '@app/hooks';
import { generateRouteParams } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Route, URLQuery } from '@app/constants';

import { Navigation } from './Navigation';
import { SearchOptionsDropdown } from './SearchOptionsDropdown';
import { AutoComplete } from './AutoComplete';

import css from './Header.module.scss';

interface HeaderProps {
  isSearchVisible?: boolean;
}

export const Header: FC<HeaderProps> = ({ isSearchVisible = true }) => {
  const queryParams = useQueryParams();
  const advancedSearchQueryParam = 'advancedSearch';

  const { searchBar } = useBranding();
  const {
    state: { query },
    searchOption,
    setSearchOption,
  } = useSearchContext();
  const { $t } = useIntl();

  const handleSortOptionsSelect = useCallback(
    ({ value }) => {
      setSearchOption(value);
    },
    [setSearchOption]
  );

  return (
    <header
      aria-label={$t({
        id: 'navigation.title',
        defaultMessage: 'Site navigation and search',
      })}
    >
      <Navigation />
      {isSearchVisible && (
        <div className={css.searchBarWrapper}>
          <div
            className={cn(css.searchBar, 'container')}
            role="search"
            data-auto="search-bar"
          >
            <SearchOptionsDropdown
              option={searchOption}
              onSelect={handleSortOptionsSelect}
            />
            <AutoComplete value={query} placeholder={searchBar.text} />
            <Link
              className={cn(
                'eb-button eb-button--flat-icon',
                css.advancedSearchButton
              )}
              to={generateRouteParams({
                pathname: Route.advancedSearch,
                query: omit(queryParams, advancedSearchQueryParam) as URLQuery,
              })}
            >
              {$t(sharedMessages.advancedSearch)}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
